import React, { useState, useEffect, useContext, useRef, forwardRef, useImperativeHandle } from "react";
import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Textarea,
  Text,
  VStack,
  Button,
  ButtonGroup,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { WebAPIContext } from "../../contexts/WebAPIContext";
import { WorkflowId, ReviewPhasePresets } from "../../contexts/constants";
import { ConfirmorInfo, PagePermissions } from "../../contexts/types";
import { useAppSelector } from "../../store/hooks";
import { getConfirmor, updateConfirm } from "../../utils/workflow";
import ConfirmorList from "./ConfirmorList";
import { ProjectContext, getPermissions } from "./InvestmentScreeningDetail";

export interface ReviewFutureHandle {
  getSnapshot: () => Record<string, string>;
  applySnapshot: (data: Record<string, string>) => void;
}

interface ReviewFutureProps {
  projectId: string;
  reviewDate: string | null;
}

interface Confirmor {
  reviewFuture?: {
    companyDivision?: ConfirmorInfo[];
    companyHeadDivision?: ConfirmorInfo[];
    salesDivision?: ConfirmorInfo[];
    procurementDivision?: ConfirmorInfo[];
  };
}

const FUTURE_ITEM_IDS = [
  "further_sales",
  "further_productivity",
  "sales_corrective",
  "manufacturing_corrective",
  "consultation",
  "strategy",
  "achievements",
  "continued_investment",
] as const;

const ReviewFuture = forwardRef<ReviewFutureHandle, ReviewFutureProps>(({ projectId, reviewDate }, ref) => {
  const { t } = useTranslation();
  const WebAPI = useContext(WebAPIContext);
  const toast = useToast();
  const TITLE = t("features.InvestmentScreening.Review.ReviewFuture.title");

  /** ログインユーザー：従業員ID */
  const employeeId = useAppSelector((state) => state.employee.employeeId);
  /** ログインユーザー：部署ID */
  const divisionId = useAppSelector((state) => state.employee.divisionId);
  /** ユーザー権限 */
  const projectContext = useContext(ProjectContext);
  const userPermissions = projectContext?.userPermissions ?? [];

  const [values, setValues] = useState<Record<string, string>>({});

  // 一時保存対象（1項目）: values（8テキスト: further_sales / further_productivity / sales_corrective /
  //   manufacturing_corrective / consultation / strategy / achievements / continued_investment）
  useImperativeHandle(ref, () => ({
    getSnapshot: () => ({ ...values }),
    applySnapshot: (data: Record<string, string>) => {
      setValues(prev => ({ ...prev, ...data }));
    },
  }), [values]);

  const [pastReviews, setPastReviews] = useState<{ label: string; date: string; data: Record<string, string> }[]>([]);
  const [activeTab, setActiveTab] = useState<"current" | number>("current");

  /** 権限 */
  const [pagePermissions, setPagePermissions] = useState<PagePermissions>();
  /** 起案者部署ID（確認ボタン活性判定用） */
  const [registrantDivisionId, setRegistrantDivisionId] = useState<string | null>(null);
  /** 確認担当者 */
  const [confirmor, setConfirmor] = useState<Confirmor | null>(null);
  /** 確認ボタン横幅 */
  const [buttonWidths, setButtonWidths] = useState<Record<string, number>>({});
  /** 確認ボタン下担当者名 */
  const [names, setNames] = useState<Record<string, ConfirmorInfo[]>>({
    companyDivision: [],
    companyHeadDivision: [],
    salesDivision: [],
    procurementDivision: [],
  });
  /** 確認元部署 */
  const [confirmSource, setConfirmSource] = useState<string>("");
  /** 保存中フラグ */
  const [isSaving, setIsSaving] = useState(false);
  /** 確認ボタン ref */
  const refs: Record<string, React.RefObject<HTMLButtonElement>> = {
    companyDivision: useRef<HTMLButtonElement>(null),
    companyHeadDivision: useRef<HTMLButtonElement>(null),
    salesDivision: useRef<HTMLButtonElement>(null),
    procurementDivision: useRef<HTMLButtonElement>(null),
  };
  const { isOpen: isOpenConfirmModal, onOpen: onOpenConfirmModal, onClose: onCloseConfirmModal } = useDisclosure();

  useEffect(() => {
    const perms = getPermissions(userPermissions, WorkflowId.ReviewFuture, undefined, employeeId, divisionId, registrantDivisionId ?? undefined);
    setPagePermissions(perms);
  }, [userPermissions, employeeId, divisionId, registrantDivisionId]);

  useEffect(() => {
    const widths: Record<string, number> = Object.keys(refs).reduce((acc, key) => {
      acc[key] = refs[key]?.current?.offsetWidth || 0;
      return acc;
    }, {} as Record<string, number>);
    setButtonWidths(widths);
  }, []);

  useEffect(() => {
    if (!projectId) return;
    const fetchConfirmorData = async () => {
      try {
        const data = await getConfirmor(WebAPI, projectId, reviewDate);
        setConfirmor(data);
      } catch { /* スルー */ }
    };
    fetchConfirmorData();
  }, [projectId, reviewDate]);

  useEffect(() => {
    if (confirmor?.reviewFuture) {
      setNames({
        companyDivision: confirmor.reviewFuture.companyDivision ?? [],
        companyHeadDivision: confirmor.reviewFuture.companyHeadDivision ?? [],
        salesDivision: confirmor.reviewFuture.salesDivision ?? [],
        procurementDivision: confirmor.reviewFuture.procurementDivision ?? [],
      });
    }
  }, [confirmor]);

  const handleModalOpen: React.MouseEventHandler<HTMLButtonElement> = (event) => {
    event.currentTarget.blur();
    if (!reviewDate) {
      toast({ title: TITLE, description: t("common.message.reviewDateNotSet"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    if (!Object.values(values).some(v => v?.trim())) {
      toast({ title: TITLE, description: t("features.InvestmentScreening.Review.common.fillContentBeforeConfirm"), status: 'warning', duration: 5000, isClosable: true });
      return;
    }
    const { name } = event.target as HTMLButtonElement;
    setConfirmSource(name);
    onOpenConfirmModal();
  };

  const handleConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    setIsSaving(true);
    try {
      const futureOutlook = JSON.stringify(values);
      const saveResp = await (WebAPI as any).request({
        query: `
          mutation putReview($input: PutReviewInput!) {
            putReview(input: $input) { status message }
          }
        `,
        variables: { input: { projectId, reviewDate, futureOutlook } }
      });
      const saveResult = saveResp?.data?.putReview;
      if (saveResult?.status !== 'success') {
        toast({ title: TITLE, description: saveResult?.message ? t(saveResult.message) : t("common.message.saveFailed"), status: 'error', duration: 5000, isClosable: true });
        return;
      }
      await updateConfirm(WebAPI, projectId, WorkflowId.ReviewFuture, employeeId, confirmSource, "true", reviewDate);
      const data = await getConfirmor(WebAPI, projectId, reviewDate);
      setConfirmor(data);
      toast({ title: TITLE, description: t("common.message.confirmed"), status: 'success', duration: 3000, isClosable: true });
      onCloseConfirmModal();
    } catch { /* スルー */ } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;
    fetchData();
  }, [projectId, reviewDate]);

  const fetchData = async () => {
    try {
      const [listResp, projResp] = await Promise.all([
        (WebAPI as any).request({
          query: `
            query getReviewListForFuture($projectId: String!) {
              getReviewListByProjectId(projectId: $projectId) {
                reviewDate
                reviewPhase
                futureOutlook
              }
            }
          `,
          variables: { projectId }
        }),
        (WebAPI as any).request({
          query: `
            query getProjectForFuture($projectId: String, $useRDSData: Boolean) {
              getProjectById(projectId: $projectId, useRDSData: $useRDSData) {
                registrantDivisionId
              }
            }
          `,
          variables: { projectId, useRDSData: true }
        }),
      ]);

      const projData = projResp?.data?.getProjectById;
      if (projData?.registrantDivisionId) setRegistrantDivisionId(projData.registrantDivisionId);

      const reviewList: { reviewDate: string; reviewPhase?: string; futureOutlook?: string }[] =
        listResp?.data?.getReviewListByProjectId || [];

      const sorted = [...reviewList].sort((a, b) => a.reviewDate.localeCompare(b.reviewDate));

      // 今回の reviewDate のデータを values に復元
      if (reviewDate) {
        const current = sorted.find(r => r.reviewDate === reviewDate);
        if (current?.futureOutlook) {
          try {
            setValues(JSON.parse(current.futureOutlook));
          } catch { /* パース失敗は無視 */ }
        }
      }

      // 過去振り返り（今回より前のもの）をタブ用に構築
      const past = sorted
        .filter(r => reviewDate ? r.reviewDate < reviewDate : true)
        .map(r => {
          let data: Record<string, string> = {};
          if (r.futureOutlook) {
            try { data = JSON.parse(r.futureOutlook); } catch { /* skip */ }
          }
          const [year, month] = r.reviewDate.split('-');
          const phaseLabel = r.reviewPhase
            ? ReviewPhasePresets.find(p => p.value === r.reviewPhase)?.label ?? r.reviewPhase
            : t("common.label.review");
          return {
            label: phaseLabel,
            date: t("features.InvestmentScreening.Review.common.yearMonthSlash", { year, month: parseInt(month) }),
            data,
          };
        });
      setPastReviews(past);

    } catch (error) {
      console.error("ReviewFuture fetchData error:", error);
      toast({ title: TITLE, description: t("common.message.fetchFailed"), status: 'error', duration: 5000, isClosable: true });
    }
  };

  const isViewingPast = activeTab !== "current";
  const pastData = typeof activeTab === "number" ? pastReviews[activeTab]?.data : null;

  return (
    <Box
      position="fixed"
      top="10vh"
      ml="2vw"
      mr="2vw"
      left="250px"
      width="calc(96vw - 250px)"
      height="85vh"
      p={6}
      overflowY="auto"
      bg="white"
      textAlign="left"
    >
      <VStack align="start" spacing={3} mb={6}>
        <Text fontSize="sm" color="gray.600">
          {t("features.InvestmentScreening.Review.ReviewFuture.description")}
        </Text>
      </VStack>

      {/* タブ切り替え: 今回の入力 / 過去の振り返り */}
      {pastReviews.length > 0 && (
        <Box mb={4}>
          <ButtonGroup size="sm" isAttached>
            <Button
              colorScheme={activeTab === "current" ? "blue" : "gray"}
              variant={activeTab === "current" ? "solid" : "outline"}
              onClick={() => setActiveTab("current")}
            >
              {t("common.label.currentInput")}
            </Button>
            {pastReviews.map((review, idx) => (
              <Button
                key={idx}
                colorScheme={activeTab === idx ? "blue" : "gray"}
                variant={activeTab === idx ? "solid" : "outline"}
                onClick={() => setActiveTab(idx)}
              >
                {t("features.InvestmentScreening.Review.common.tabPastReview", { label: review.label, date: review.date })}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
      )}

      {/* 過去の振り返り表示時のバナー */}
      {isViewingPast && (
        <Box bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="md" p={3} mb={4}>
          <Text fontSize="sm" color="blue.700">
            {t("features.InvestmentScreening.Review.common.viewingPastReadOnly", {
              label: pastReviews[activeTab as number]?.label ?? "",
              date: pastReviews[activeTab as number]?.date ?? "",
            })}
          </Text>
        </Box>
      )}

      <Table variant="simple" size="sm" w="100%" border="1px" borderColor="gray.200">
        <Thead>
          <Tr bg={isViewingPast ? "blue.500" : "blue.600"}>
            <Th w="30%" color="white">{t("common.label.item")}</Th>
            <Th w="70%" color="white">
              {isViewingPast
                ? t("features.InvestmentScreening.Review.common.pastContentLabel", {
                    label: pastReviews[activeTab as number]?.label ?? "",
                    date: pastReviews[activeTab as number]?.date ?? "",
                  })
                : t("common.label.content")}
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {FUTURE_ITEM_IDS.map((id) => (
            <Tr key={id} borderBottom="1px" borderColor="gray.200" _hover={{ bg: "gray.50" }}>
              <Td fontWeight="semibold" fontSize="sm">
                {t(`features.InvestmentScreening.Review.ReviewFuture.items.${id}.label`)}
              </Td>
              <Td>
                {isViewingPast ? (
                  <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.700" py={2}>
                    {pastData?.[id] || t("common.label.noEntry")}
                  </Text>
                ) : (
                  <Textarea
                    size="sm"
                    placeholder={t(`features.InvestmentScreening.Review.ReviewFuture.items.${id}.placeholder`)}
                    rows={3}
                    maxLength={1000}
                    value={values[id] || ""}
                    onChange={(e) => setValues({ ...values, [id]: e.target.value })}
                  />
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {/* 確認ボタン */}
      {!isViewingPast && (
        <Flex justify="flex-end" mt={4} mb={3}>
          <Box>
            <Button
              colorScheme="blue"
              name="companyDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledCompanyDivision}
              ref={refs["companyDivision"]}
            >
              {t("common.constants.divisionConfirm.company")}
            </Button>
            <ConfirmorList confirmorInfo={names["companyDivision"]} width={buttonWidths["companyDivision"]} />
          </Box>
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              name="companyHeadDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledCompanyHeadDivision}
              ref={refs["companyHeadDivision"]}
            >
              {t("common.constants.divisionConfirm.companyHead")}
            </Button>
            <ConfirmorList confirmorInfo={names["companyHeadDivision"]} width={buttonWidths["companyHeadDivision"]} />
          </Box>
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              name="salesDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledSalesDivision}
              ref={refs["salesDivision"]}
            >
              {t("common.constants.divisionConfirm.sales")}
            </Button>
            <ConfirmorList confirmorInfo={names["salesDivision"]} width={buttonWidths["salesDivision"]} />
          </Box>
          <Box>
            <Button
              ml="10px"
              colorScheme="blue"
              name="procurementDivision"
              onClick={handleModalOpen}
              isDisabled={!pagePermissions?.eabledProcurementDivision}
              ref={refs["procurementDivision"]}
            >
              {t("common.constants.divisionConfirm.procurement")}
            </Button>
            <ConfirmorList confirmorInfo={names["procurementDivision"]} width={buttonWidths["procurementDivision"]} />
          </Box>
        </Flex>
      )}

      {/* 確認モーダル */}
      <Modal isOpen={isOpenConfirmModal} onClose={onCloseConfirmModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("common.button.save")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t("common.message.confirmSave")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} isDisabled={isSaving} isLoading={isSaving} onClick={handleConfirm}>
              {t("common.button.save")}
            </Button>
            <Button variant="ghost" onClick={onCloseConfirmModal}>{t("common.button.cancel")}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
});

export default ReviewFuture;
